/**
 * Property Discovery Tool - Badge Configurations
 *
 * Centralized badge styling configuration for consistency across the tool.
 * All property-related badges should use these configs to ensure uniform appearance.
 */

import {
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaHome,
  FaHandshake,
  FaEnvelope,
  FaSpinner,
  FaExclamationTriangle,
  FaClock,
} from "react-icons/fa";

/**
 * Generate a friendly display name for a scrape
 * Priority: 1) Custom name (displayName), 2) Legacy town field, 3) Auto-generated from postcodes, 4) Date
 */
export const getScrapeName = (scrape, options = {}) => {
  const { includeDetails = false, short = false } = options;

  if (!scrape) return "Unknown Scrape";

  // First priority: Use custom displayName if set
  if (scrape.displayName && scrape.displayName.trim()) {
    return scrape.displayName;
  }

  // Second priority: Legacy town field (for backward compatibility)
  if (scrape.town && scrape.town.trim()) {
    return scrape.town;
  }

  // Second priority: Try to use postcodes as the primary name
  if (scrape.postcodes && scrape.postcodes.length > 0) {
    const firstPostcode = scrape.postcodes[0];
    // Format postcode (e.g., "E146LS" → "E14 6LS")
    const formatted = firstPostcode.replace(
      /^([A-Z]{1,2}\d{1,2})(\d[A-Z]{2})$/i,
      "$1 $2"
    );

    if (scrape.postcodes.length > 1) {
      return short
        ? `${formatted} +${scrape.postcodes.length - 1}`
        : `${formatted} +${scrape.postcodes.length - 1} more`;
    }

    // Add search details if requested
    if (includeDetails && scrape.searchParams) {
      const details = [];
      if (scrape.searchParams.minPrice || scrape.searchParams.maxPrice) {
        const min = scrape.searchParams.minPrice
          ? `£${(parseInt(scrape.searchParams.minPrice) / 1000).toFixed(0)}k`
          : "";
        const max = scrape.searchParams.maxPrice
          ? `£${(parseInt(scrape.searchParams.maxPrice) / 1000).toFixed(0)}k`
          : "";
        if (min && max) details.push(`${min}-${max}`);
        else if (min) details.push(`${min}+`);
        else if (max) details.push(`up to ${max}`);
      }
      if (scrape.searchParams.includeSSTC) {
        details.push("inc. SSTC");
      }
      if (details.length > 0) {
        return `${formatted} (${details.join(", ")})`;
      }
    }

    return formatted;
  }

  // Fallback to date
  if (scrape.createdAt) {
    const date = scrape.createdAt.toDate
      ? scrape.createdAt.toDate()
      : new Date(scrape.createdAt);
    return `Scrape from ${date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })}`;
  }

  return "Unknown Scrape";
};

/**
 * Property Status Badge Configuration
 * Used in: PropertyTable, MailshotDetailPage, PropertyDetailPage, etc.
 */
export const PROPERTY_STATUS_CONFIG = {
  "For Sale": {
    variant: "info",
    color: "#3b82f6", // Blue
    textColor: "white",
    icon: FaHome,
    label: "For Sale",
    shortLabel: "FS",
  },
  "Sold STC": {
    variant: "warning",
    color: "#f59e0b", // Orange
    textColor: "white",
    icon: FaHandshake,
    label: "Sold STC",
    shortLabel: "SSTC",
  },
  "Under Offer": {
    variant: "purple",
    color: "#8b5cf6", // Purple
    textColor: "white",
    icon: FaEnvelope,
    label: "Under Offer",
    shortLabel: "UO",
  },
};

/**
 * Delivery Status Badge Configuration
 * Used in: PropertyTable, PropertyDetailPage, MailshotDetailPage, etc.
 */
export const DELIVERY_STATUS_CONFIG = {
  delivered: {
    variant: "success",
    color: "#10b981", // Green
    textColor: "white",
    icon: FaCheckCircle,
    label: "Delivered",
    title: "Successfully delivered",
  },
  sent: {
    variant: "info",
    color: "#3b82f6", // Blue
    textColor: "white",
    icon: FaCheckCircle,
    label: "Sent",
    title: "Mail sent, awaiting confirmation",
  },
  returned: {
    variant: "warning",
    color: "#f59e0b", // Orange
    textColor: "white",
    icon: FaTimesCircle,
    label: "Returned",
    title: "Returned to sender",
  },
  undeliverable: {
    variant: "danger",
    color: "#ef4444", // Red
    textColor: "white",
    icon: FaTimesCircle,
    label: "Undeliverable",
    title: "Cannot be delivered",
  },
  unknown: {
    variant: "default",
    color: "#6b7280", // Gray
    textColor: "white",
    icon: FaQuestionCircle,
    label: "Unknown",
    title: "Delivery status unknown",
  },
};

/**
 * Scrape Status Badge Configuration
 * Used in: ScrapesTab, ScrapeDetailPage, etc.
 */
export const SCRAPE_STATUS_CONFIG = {
  completed: {
    variant: "success",
    color: "#10b981", // Green
    textColor: "white",
    icon: FaCheckCircle,
    label: "Completed",
  },
  running: {
    variant: "info",
    color: "#3b82f6", // Blue
    textColor: "white",
    icon: FaSpinner,
    label: "Running",
  },
  failed: {
    variant: "danger",
    color: "#ef4444", // Red
    textColor: "white",
    icon: FaExclamationTriangle,
    label: "Failed",
  },
};

/**
 * Mailshot Status Badge Configuration
 * Used in: MailshotsTab, MailshotDetailPage, CampaignsTab, etc.
 */
export const MAILSHOT_STATUS_CONFIG = {
  sent: {
    variant: "success",
    color: "#10b981", // Green
    textColor: "white",
    icon: FaCheckCircle,
    label: "Sent",
  },
  pending: {
    variant: "warning",
    color: "#f59e0b", // Orange
    textColor: "white",
    icon: FaClock,
    label: "Pending",
  },
};

/**
 * Helper function to get property status config
 */
export const getPropertyStatusConfig = (status) => {
  return (
    PROPERTY_STATUS_CONFIG[status] || {
      variant: "default",
      color: "#6b7280",
      textColor: "white",
      label: status,
    }
  );
};

/**
 * Helper function to get delivery status config
 */
export const getDeliveryStatusConfig = (status) => {
  return DELIVERY_STATUS_CONFIG[status] || DELIVERY_STATUS_CONFIG.unknown;
};

/**
 * Helper function to get scrape status config
 */
export const getScrapeStatusConfig = (status) => {
  return (
    SCRAPE_STATUS_CONFIG[status] || {
      variant: "default",
      color: "#6b7280",
      textColor: "white",
      icon: FaQuestionCircle,
      label: status || "Unknown",
    }
  );
};

/**
 * Helper function to get mailshot status config
 * Accepts status string or boolean mailedOut field
 */
export const getMailshotStatusConfig = (statusOrMailedOut) => {
  // Handle boolean mailedOut field
  if (typeof statusOrMailedOut === "boolean") {
    return statusOrMailedOut
      ? MAILSHOT_STATUS_CONFIG.sent
      : MAILSHOT_STATUS_CONFIG.pending;
  }
  // Handle string status
  return (
    MAILSHOT_STATUS_CONFIG[statusOrMailedOut] || MAILSHOT_STATUS_CONFIG.pending
  );
};

/**
 * Sort order for property statuses
 */
export const PROPERTY_STATUS_SORT_ORDER = {
  "For Sale": 1,
  "Sold STC": 2,
  "Under Offer": 3,
};
